<template>
  <div v-if="$apollo.queries.post.loading">
    <div class="w-full h-[400px] bg-gray-lightest animate-pulse" role="status">
      <span class="sr-only">Loading...</span>
    </div>
    <div class="container my-9.6">
      <BreadcrumbsSkeleton class="my-10" />

      <div class="flex flex-col-reverse gap-16 lg:flex-row">
        <aside class="lg:w-1/3">
          <div class="flex flex-col animate-pulse" role="status">
            <div class="bg-gray-lightest h-9.6"></div>
            <div class="w-full mt-4 space-y-4">
              <div
                v-for="k in 3"
                :key="k"
                class="h-9.6 bg-gray-lightest animate-pulse rounded"
              ></div>
            </div>
            <span class="sr-only">Loading...</span>
          </div>

          <div class="mt-10">
            <ActionSkeleton />
          </div>
        </aside>

        <div class="lg:w-2/3" role="status">
          <ArticleSkeleton />
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <!-- Visual -->
    <VisualHero
      v-if="rebrandingVisual"
      preload
      :image="visualImage"
      background-color="bg-green-sell"
    >
      <template slot="title"> {{ post?.title }}</template>
    </VisualHero>
    <Visual v-else preload :title="post?.title" :image="visualImage" />

    <!-- Navigation -->
    <div class="container mx-auto my-9.6">
      <!-- Breadcrumbs -->
      <div class="flex my-10">
        <Breadcrumbs class="flex-grow" :items="breadcrumbItems" />
        <Sharers class="flex-shrink-0" :title="post?.title" />
      </div>

      <div class="flex flex-col-reverse gap-16 lg:flex-row">
        <!-- Sidebar 1/3 -->
        <aside class="relative lg:w-1/3">
          <div class="pt-4 bg-white lg:sticky lg:top-28">
            <div v-if="relatedSearches" class="flex flex-col mb-16">
              <h2 class="font-bold uppercase text-paragraph">
                Ricerche correlate
              </h2>
              <ul class="w-full mt-4 divide-y-1 divide-gray-lighter">
                <li
                  v-for="(item, k) in relatedSearches"
                  :key="k"
                  class="w-full"
                >
                  <NuxtLink
                    :to="item.url"
                    class="flex w-full py-4 text-sm font-bold uppercase text-blue hover:text-blue-dark"
                  >
                    <span class="truncate">{{ item.text }}</span>
                    <SvgIcon
                      class="flex-initial w-6.4 h-6.4 ml-2"
                      aria-hidden="true"
                      name="angle-right-blue"
                    />
                  </NuxtLink>
                </li>
              </ul>
            </div>

            <div v-if="commercialAdMobile" class="mb-16">
              <Action
                :highlight="true"
                size="small"
                v-bind="commercialAdMobile"
              >
                <template #action>
                  <a
                    v-if="commercialAdMobile.action"
                    class="flex items-center mt-5 text-sm font-bold text-white uppercase"
                    v-bind="{
                      ...(commercialAdMobile.action.url && {
                        href: commercialAdMobile.action.url,
                        target: commercialAdMobile.action.target,
                        rel: 'noopener',
                      }),
                    }"
                  >
                    {{ commercialAdMobile.action.text }}
                    <SvgIcon
                      name="angle-right-white"
                      aria-hidden="true"
                      class="ml-4 h-5.6 w-5.6 inline-block"
                    />
                  </a>
                </template>
              </Action>
            </div>

            <div class="mb-16">
              <Action
                title="Numero verde"
                content="Da telefono fisso e cellulare 800.89.69.68 (dall'estero +39 02 84251701), attivo dal lunedì al venerdì dalle 9.00 alle 17.00"
                class="my-9.6"
              >
                <template #icon>
                  <SvgIcon
                    name="phone"
                    aria-hidden="true"
                    class="h-9.6 w-9.6"
                  />
                </template>
              </Action>
            </div>

            <div class="mb-16">
              <a
                :href="`${$config.baseURL}/valuta-casa`"
                class="w-full bg-blue hover:bg-blue-dark flex flex-row py-[13px] px-8 rounded text-white font-bold text-subtitle items-center"
              >
                Valuta la tua casa
              </a>
            </div>
          </div>
        </aside>
        <!-- Article 1/3 -->
        <div class="lg:w-2/3">
          <article
            v-dompurify-html="post?.content"
            class="prose max-w-none"
          ></article>

          <template v-if="commercialAd">
            <component
              :is="commercialAd.link ? 'a' : 'div'"
              class="hidden my-10 lg:block"
              v-bind="{
                ...(commercialAd.link && {
                  href: commercialAd.link,
                  target: '_blank',
                  rel: 'noopener',
                }),
              }"
            >
              <NuxtImg
                v-if="commercialAd.image"
                :src="commercialAd.image.src"
                :alt="commercialAd.image.alt ?? ''"
                class="w-full h-full"
                width="773"
                height="267"
                sizes="sm:520 md:773"
                fit="contain"
                :modifiers="{ b: '#ffffff' }"
                format="webp"
                loading="lazy"
              />
            </component>
          </template>
          <div class="my-16">
            <Accordion
              v-for="(item, index) of accordions"
              :key="`NewsAccordions_${index}`"
              :heading="item.title"
              class="ucrs-accordion--noBorderTop"
              content-class="max-h-300"
            >
              <div v-dompurify-html="item.content"></div>
            </Accordion>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col-reverse justify-between my-16 lg:flex-row gap-9.6"
      >
        <div
          v-if="computedTags"
          class="flex flex-wrap items-center gap-16 flex-grow-2"
        >
          <p class="text-sm font-bold uppercase text-black-light">Tags</p>
          <NuxtLink
            v-for="(tag, k) in computedTags"
            :key="k"
            :to="tag.url"
            type="button"
            class="px-8 py-4 text-sm font-bold uppercase rounded bg-gray-lightest hover:bg-gray-lighter text-black-light"
          >
            {{ tag.text }}
          </NuxtLink>
        </div>
        <div v-if="post" class="flex-grow">
          <p class="lg:text-right text-gray">
            {{ post?.date | formatDate }}
          </p>
          <meta itemprop="datePublished" :content="post?.dateGmt" />
        </div>
      </div>

      <div v-if="loadingNavigation" class="flex flex-col gap-16 lg:flex-row">
        <ActionSkeleton />
        <ActionSkeleton />
      </div>
      <div v-else class="flex flex-col gap-16 lg:flex-row">
        <Action v-if="prevPost" title="Precedente" class="w-full lg:w-1/2">
          <template #content>
            <p>{{ prevPost.title }}</p>
            <NuxtLink
              :to="$utilities.getRelativeUrl(prevPost.link)"
              class="flex items-center pt-4 font-bold uppercase text-navitem text-blue"
            >
              Leggi articolo
              <SvgIcon
                aria-hidden="true"
                name="angle-right-blue"
                class="ml-4 max-h-5 max-w-5"
              />
            </NuxtLink>
          </template>
        </Action>
        <div v-else class="w-1/2"></div>

        <Action v-if="nextPost" title="Successivo" class="w-full lg:w-1/2">
          <template #content>
            <p>{{ nextPost.title }}</p>
            <NuxtLink
              :to="$utilities.getRelativeUrl(nextPost.link)"
              class="flex items-center pt-4 font-bold uppercase text-navitem text-blue"
            >
              Leggi articolo
              <SvgIcon
                aria-hidden="true"
                name="angle-right-blue"
                class="ml-4 max-h-5 max-w-5"
              />
            </NuxtLink>
          </template>
        </Action>
        <div v-else class="w-1/2"></div>
      </div>

      <div v-if="relatedArticles?.length" class="my-9.6">
        <h2 class="font-bold uppercase text-paragraph">
          {{ options?.titoloBloccoArticoli }}
        </h2>

        <Swiper class="mt-9.6" :options="swiperOptions">
          <PostCard
            v-for="(post, k) in relatedArticles"
            :key="k"
            :post="post"
            type="vertical"
            class="w-full swiper-slide"
            category-size="small"
          />
        </Swiper>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import Accordion from '~/components/Accordion.vue'
import Action from '~/components/News/Action.vue'
import ActionSkeleton from '~/components/News/ActionSkeleton.vue'
import ArticleSkeleton from '~/components/News/ArticleSkeleton.vue'
import Breadcrumbs from '~/components/News/Breadcrumbs.vue'
import BreadcrumbsSkeleton from '~/components/News/BreadcrumbsSkeleton.vue'
import PostCard from '~/components/News/PostCard.vue'
import Sharers from '~/components/News/Sharers.vue'
import Swiper from '~/components/Swiper.vue'
import Visual from '~/components/News/Visual.vue'
import VisualHero from '~/components/VisualHero.vue'

// GraphQL
import settings from '~/apollo/queries/settings.gql'
import post from '~/apollo/queries/post.gql'
import currentPostCursor from '~/apollo/queries/currentPostCursor.gql'
import postNavigation from '~/apollo/queries/postNavigation.gql'
import posts from '~/apollo/queries/posts.gql'

export default {
  components: {
    Accordion,
    Action,
    ActionSkeleton,
    ArticleSkeleton,
    Breadcrumbs,
    BreadcrumbsSkeleton,
    PostCard,
    Sharers,
    Swiper,
    Visual,
    VisualHero,
  },

  apollo: {
    settings: {
      query: settings,
      prefetch: true,
      update(data) {
        return data
      },
    },
    cursor: {
      query: currentPostCursor,
      prefetch: false,
      variables() {
        return { id: this.post.databaseId }
      },
      update(data) {
        return data.posts?.edges[0]?.cursor
      },
      skip() {
        return !this.post
      },
    },
    navigation: {
      query: postNavigation,
      prefetch: false,
      variables() {
        return {
          cursor: this.cursor,
          categoryIn: [this.post.categories.nodes[0].id],
        }
      },
      update(data) {
        return {
          prev: data.prev?.nodes[0],
          next: data.next?.nodes[0],
        }
      },
      skip() {
        return !this.cursor
      },
    },
    posts: {
      query: posts,
      prefetch: false,
      variables() {
        return {
          first: 5,
          notIn: [this.post?.id],
        }
      },
      skip() {
        return !this.post?.id
      },
    },
    post: {
      query: post,
      prefetch: ({ route }) => ({ id: route.params.slug, idType: 'SLUG' }),
      variables() {
        return { id: this.$route.params.slug, idType: 'SLUG' }
      },
      update(data) {
        if (!data?.post) {
          this.$nuxt.error({ statusCode: 404, message: 'Page not found' })
        }
        return data?.post
      },
    },
  },
  filters: {
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
      return new Date(date).toLocaleDateString('it-IT', options)
    },
  },

  data() {
    return {
      swiperOptions: {
        observer: true,
        observeParents: true,
        watchOverflow: true,
        slidesPerView: 1.2,
        spaceBetween: 15,
        grabCursor: true,
        loopAdditionalSlides: 1,
        loop: false,
        breakpoints: {
          768: {
            slidesPerView: 2.2,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 40,
          },
        },
      },
    }
  },

  head() {
    return {
      title: this.seoTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.post?.seo.metaDesc,
        },
        {
          hid: 'og:locale',
          name: 'og:locale',
          content: 'it_IT',
        },
        {
          hid: 'og:type',
          name: 'og:type',
          content: this.post?.seo?.opengraphType,
          skip: !this.post?.seo?.opengraphType,
        },
        {
          hid: 'og:title',
          name: 'og:title',
          content: this.post?.seo?.opengraphTitle,
          skip: !this.post?.seo?.opengraphTitle,
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.post?.seo?.opengraphDescription,
          skip: !this.post?.seo?.opengraphDescription,
        },
        {
          hid: 'og:url',
          name: 'og:url',
          content: this.post?.seo?.opengraphUrl,
          skip: !this.post?.seo?.opengraphUrl,
        },
        {
          hid: 'og:site_name',
          name: 'og:site_name',
          content: this.post?.seo?.opengraphSiteName,
          skip: !this.post?.seo?.opengraphSiteName,
        },
        {
          hid: 'article:published_time',
          name: 'article:published_time',
          content: this.post?.seo?.opengraphPublishedTime,
          skip: !this.post?.seo?.opengraphPublishedTime,
        },
        {
          hid: 'article:modified_time',
          name: 'article:modified_time',
          content: this.post?.seo?.opengraphModifiedTime,
          skip: !this.post?.seo?.opengraphModifiedTime,
        },
        {
          hid: 'og:image',
          name: 'og:image',
          content: this.post?.seo?.opengraphImage?.mediaItemUrl,
          skip: !this.post?.seo?.opengraphImage?.mediaItemUrl,
        },
        {
          hid: 'og:image:width',
          name: 'og:image:width',
          content: this.post?.seo?.opengraphImage?.mediaDetails?.width,
          skip: !this.post?.seo?.opengraphImage?.mediaDetails?.width,
        },
        {
          hid: 'og:image:height',
          name: 'og:image:height',
          content: this.post?.seo?.opengraphImage?.mediaDetails?.height,
          skip: !this.post?.seo?.opengraphImage?.mediaDetails?.height,
        },
        {
          hid: 'og:image:type',
          name: 'og:image:type',
          content: this.post?.seo?.opengraphImage?.mimeType,
          skip: !this.post?.seo?.opengraphImage?.mimeType,
        },
        {
          hid: 'author',
          name: 'author',
          content: this.post?.author?.node?.name,
          skip: !this.post?.author?.node?.name,
        },
        {
          hid: 'twitter:label1',
          name: 'twitter:label1',
          content: 'Scritto da',
        },
        {
          hid: 'twitter:data1',
          name: 'twitter:data1',
          content: this.post?.author?.node?.name || 'UniCredit RE Services',
        },
        {
          hid: 'twitter:label2',
          name: 'twitter:label2',
          content: 'Tempo di lettura stimato',
        },
        {
          hid: 'twitter:data2',
          name: 'twitter:data2',
          content: this.post?.seo?.readingTime || '3 minuti',
        },
      ],
      script: [
        {
          type: 'application/ld+json',
          json: this.schema,
        },
        {
          type: 'text/javascript',
          innerHTML: `var utag_data = ${JSON.stringify({
            site_name: this.settings?.generalSettings?.title,
            site_description: this.settings?.generalSettings?.description ?? '',
            page_type: 'post',
            post_id: this.post?.databaseId,
            post_title: this.post?.title,
            post_category: this.post?.categories?.nodes.map(
              (item) => item.name
            ),
            post_tags: this.post?.tags?.nodes.map((item) => item.name),
            post_author: this.post?.author?.node?.name,
            post_date: this.post?.dateGmt?.split('T')[0].replace(/-/g, '/'),
            user_role: 'guest',
          })}
          `,
        },
      ],
      __dangerouslyDisableSanitizers: ['innerHTML'],
    }
  },
  computed: {
    seoTitle() {
      return this.post?.seo?.title ?? this.post?.title
    },
    excerpt() {
      return this.post?.excerpt
    },
    options() {
      return this.settings?.acfOptions.acfOptions
    },

    visualImage() {
      if (!this.post?.featuredImage?.node.mediaItemUrl) {
        return
      }

      const image = this.$utilities.imgAlias(
        this.post?.featuredImage?.node.mediaItemUrl
      )

      return this.rebrandingVisual ? image : this.$img(image, { height: 450 })
    },

    relatedSearches() {
      return this.post?.acfCommon?.ricercheCorrelate?.map((item) => ({
        text: item.testo,
        url: `${new URL(item.link).pathname}`,
      }))
    },
    relatedArticles() {
      return this.post?.acfCommon?.contenutiEditorialiParalleli?.map((item) =>
        this.$utilities.mapPost(item)
      )
    },
    schema() {
      return this.post && JSON.parse(this.post.seo.schema.raw)
    },
    breadcrumbItems() {
      return this.post?.seo.breadcrumbs.map((item) => ({
        text: item.text,
        url: new URL(item.url).pathname,
      }))
    },
    loadingNavigation() {
      return (
        this.$apollo.queries.post.loading ||
        this.$apollo.queries.cursor.loading ||
        this.$apollo.queries.navigation.loading
      )
    },
    prevPost() {
      return this.navigation?.prev
    },
    nextPost() {
      return this.navigation?.next
    },
    commercialAd() {
      if (!this.post?.acfFields?.bannerCommerciale?.length) return null
      return this.post?.acfFields?.bannerCommerciale.map((item) => ({
        title: item.titolo,
        subtitle: item.contenuto,
        ...(item.immagine && {
          image: {
            src: this.$utilities.imgAlias(item.immagine.mediaItemUrl),
            alt: item.immagine.altText,
          },
        }),
        ...(item.link && {
          link: item.link.url,
          target: item.link.target,
        }),
      }))[0]
    },
    accordions() {
      return this.post?.acfFields?.accordion?.map((it) => ({
        title: it.titolo,
        content: it.contenuto,
      }))
    },
    commercialAdMobile() {
      const ad = this.post?.acfFields?.bannerCommercialeMobile
      return (
        ad && {
          title: ad.testoBanner,
          ...(ad.linkBanner && {
            action: {
              text: ad.linkBanner.title,
              url: ad.linkBanner.url,
              target: ad.linkBanner.target ?? '_blank',
            },
          }),
        }
      )
    },
    computedTags() {
      return (
        this.post?.tags?.nodes.length &&
        this.post?.tags?.nodes.map((item) => ({
          text: item.name,
          url: `/news/tag/${item.slug}/`,
        }))
      )
    },

    rebrandingVisual() {
      const { $route } = this
      const { path } = $route
      const enabledPages = [
        '/news/come-fare/come-vendere-casa-rapidamente/',
        '/news/come-fare/documenti-vendita-casa/',
      ]
      const rebrandingVisual = enabledPages.includes(path)

      return rebrandingVisual
    },
  },
}
</script>
